// Source de cadrage minimale (structurelle) : tout objet exposant `getBounds()`. On évite ainsi le
// type `Map` de mapbox-gl, qui se résout différemment selon le package (ui peerDep vs apps) et
// provoque des conflits de types ("excessive stack depth").
export interface BboxSource {
  getBounds: () => {
    getWest: () => number;
    getEast: () => number;
    getNorth: () => number;
    getSouth: () => number;
  } | null | undefined;
  // `getBounds()` (mapbox) rogne le viewport du padding caméra ; `unproject` des coins donne le viewport réel.
  unproject?: (point: [number, number]) => { lng: number; lat: number };
  getCanvas?: () => { clientWidth: number; clientHeight: number };
}

export interface MapBbox {
  nwLat: number;
  nwLng: number;
  seLat: number;
  seLng: number;
}

// Teste l'appartenance d'un point (longitude dans [-180, 180]) à la bbox. Les longitudes de la bbox
// sont brutes (continues, possiblement hors [-180, 180] à cheval sur l'antiméridien) : on ramène la
// longitude du point dans la fenêtre [nwLng, nwLng + 360[ avant de comparer à l'est.
export function bboxContains(bbox: MapBbox, longitude: number, latitude: number): boolean {
  if (latitude < bbox.seLat || latitude > bbox.nwLat) return false;
  let lng = longitude;
  while (lng < bbox.nwLng) lng += 360;
  while (lng >= bbox.nwLng + 360) lng -= 360;
  return lng <= bbox.seLng;
}

interface UseAppMapBboxOptions {
  debounceMs?: number;
  onBeforeChange?: () => void;
}

function round4(n: number): number {
  return Math.round(n * 1e4) / 1e4;
}

interface ViewportBounds {
  west: number;
  east: number;
  north: number;
  south: number;
}

function viewportBounds(map: BboxSource): ViewportBounds | undefined {
  if (map.unproject && map.getCanvas) {
    const canvas = map.getCanvas();
    const { clientWidth: width, clientHeight: height } = canvas;
    if (width > 0 && height > 0) {
      const nw = map.unproject([0, 0]);
      const ne = map.unproject([width, 0]);
      const sw = map.unproject([0, height]);
      return { west: nw.lng, east: ne.lng, north: nw.lat, south: sw.lat };
    }
  }
  const bounds = map.getBounds();
  if (!bounds) return undefined;
  return { west: bounds.getWest(), east: bounds.getEast(), north: bounds.getNorth(), south: bounds.getSouth() };
}

// Suit le cadrage (bbox) d'une carte Mapbox : `update()` (branché sur `move-end`) recalcule les
// coins, débouncé, et n'émet que sur changement réel. Longitudes BRUTES (continues, non repliées
// dans [-180, 180]) : replier une vue dézoomée à cheval sur l'antiméridien inverserait la bbox en
// une bande étroite. Le cas « monde entier » (span >= 360°) est ramené à [-180, 180] ; lat clampée ±90°.
export function useAppMapBbox(getMap: () => BboxSource | undefined, options: UseAppMapBboxOptions = {}) {
  const debounceMs = options.debounceMs ?? 500;
  const nwLat = ref<number>();
  const nwLng = ref<number>();
  const seLat = ref<number>();
  const seLng = ref<number>();

  let timeout: ReturnType<typeof setTimeout> | undefined;

  function update() {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => {
      const map = getMap();
      if (!map) return;
      const view = viewportBounds(map);
      if (!view) return;
      const rawWest = view.west;
      // Mapbox renvoie l'est en lng continu (>= ouest) ; on dé-croise par sécurité si une projection
      // repliait l'est sous l'ouest (vue à cheval sur l'antiméridien) avant de mesurer le span.
      let rawEast = view.east;
      if (rawEast < rawWest) rawEast += 360;
      const spansWorld = rawEast - rawWest >= 360;
      const next = {
        nwLat: round4(Math.min(90, view.north)),
        nwLng: round4(spansWorld ? -180 : rawWest),
        seLat: round4(Math.max(-90, view.south)),
        seLng: round4(spansWorld ? 180 : rawEast),
      };
      if (next.nwLat === nwLat.value && next.nwLng === nwLng.value && next.seLat === seLat.value && next.seLng === seLng.value) return;
      options.onBeforeChange?.();
      nwLat.value = next.nwLat;
      nwLng.value = next.nwLng;
      seLat.value = next.seLat;
      seLng.value = next.seLng;
    }, debounceMs);
  }

  onBeforeUnmount(() => {
    if (timeout) clearTimeout(timeout);
  });

  return { nwLat, nwLng, seLat, seLng, update };
}
